import {addDays, formatISO, startOfDay, subDays} from 'date-fns';
import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
} from '../../services/schedularService';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
} from './bookingMachine.types';
import {canNavigateNextWeek} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {TZDate} from '@date-fns/tz';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';


export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
        };
      }) =>
        getAvailability(input.templateId, {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
        }),
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
        };
      }) =>
        noAvailability(input.templateId, {
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange} = context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange, timezone} =
        context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }
      const noSlotsAvailable =
        Object.keys(timeSlots.free_time_slots || {}).length === 0;

      const canNavigateToNextWeek = canNavigateNextWeek(
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        noSlotsAvailable,
      );

      return noSlotsAvailable && !canNavigateToNextWeek;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAdAIYBuhWANochVgC4CeASoXmADKqES5QDEEqHGHy5imYWkw8ipClRoNmrDlx4JRqAMaFaWQQG0ADAF0jxxKAAOqWHT04LIAB6IAtACYAjAA58Adk9Awz9Db3cANgBmPwAWbwAaEHpET1CATnxIsLSAVjTvQxzIyJjwgF8yxMlsPBkySmpyOiYWGBVuPF4wACdu1G78S0paADN+gFt8aukSevkmxVb2Tg6odRwxbV0DEzNHa1tthyRnRBycv3xUwyzgrJi-d0TkhE93Q0MruNjvHO8Y6IlCpVdA1AgQHRgAAqWHGYAAClhNBgerxYLRCN1aAARSEAeRGAHUwGAMPhNAALJZ7E4HOyCRwuBAeQL4QwAt6RYJ+EoxHLPFJ-GJsmJpOI5CIxTxpHzAkDTWoQ2jQ2EIpEo7r8SHkqmsGlWGz046gJmuSLudz+c4fALmtLhHKeflJRDvG5XcJvcLesXecJ+PxyhXgyEwuGI5GogDuJLJQictH1IDpR0ZbiKwr8aWzaVCDwdhieLteYXCbJl7n+aRCPJyMSDoOkSpV4fV0djg26YGISZT9jTzLrnnw4XeuS5MuKjwFCC8H3w2fCwXcaXcfm93gbUkVodVEY1dTkjWa7R4-EEwg0KKmjZ3yrDasjA1mR4U9FPeHWmx09jMvcNqYnEygSRP4fLhP8o4xCulaRDOPjhMKDp+jynpeH4RRbmC+DNg++49IeDRvh+fA9H0AxDDoYzdJMwY4burZPoR8wnisagaFsv67CY+wAf2QGIOulp2tmnjRN6ngPPBMShGylYfH6lb-E6WFNgxj4Hi+RELO+KyQLw-6HPxJqIN6bJ1hE3gBjcVlrjOXjuDkC6POcAaREODyqXeLYaQRWksQw7T6fonjmLSfEMgJCABmWDpOn6Mk5MEaT2WJTkYWKfjeKu7jQTkXkhvee5ts+sjaaxXDBe4YUGkZkUmQgvygRhAQWp4MV-HBxYWoYw4ROy3reu40SeZU8q3oVPn4aVczHgwACivT9LwXa0N09CGUaA6ROEGSIR8aSAnESkxNJK74I5fIyg8K47e4BX0UVjGaWVAX0AAyuQqC0LAvC6HCsBfbQ+CwAArsg4x0JtgENXW+AyfkPirkUUHhPZoqfBKEGrgC2axJED24cVTH+XNH1A79-1wEDIPg5DiahbxdXGqcjWemyS4SYYvqHX46O5vgWPZblkR4wChPqdN+BU4D30AMKCCMWA0T+gh-aqsvA5oivK+M0PGaz10Lj63g7d8JTOi8a5w-ahbZYYo6eIhEtPb5Awy0DCs4ErKtHOrAM02DENQzx4XMwOiFOdW0ShIhiEBvZATDuueQSnyJRri7U0lfgXawJSkCg+QYAAGITO9dN0LonQCEIIgbOIN7bpNeE53nBcQEXpfl5XtDV2sHGqzgf6h7VW1RRWmQ8m81YQa5ngzhnlq-G18UxHE5RjXRRPPQR7cUoXxdlzRFfB33Z5kf0gzDNRtETY92dMfvh-dyfvf91+WhDyPNXJhFLPATEp8f4qEChvGCOveyMpLirjAaEbmoRPBZ1bk-OAHcu7H3GItciBlR5-3DlFJ2pQFwFADE6a4UoixW0eJaB2dodpZhXMg4mB5n6dyPhMbBy0Qq-z7PVVmkkojw2CH6e0YRUjekThaDmJQnZOntE7Zhu8Zqvh0gAOW+lgEY9BeA4E0dogAEjYYGUBUD634cBB4nxubpwwg7X4aNupiVAklTK9oIKik3iCZuD8UGsLAGtJgaCD7sLAP7am31yQ6xouYgBKQJFXGzNBfaYRhqOOodKfA3gxIyUiD4HkDilFu3wKDHApNi4Pk+t9Y+jBgkvxWmAEYATKQAEFXpkxqXU0J+BTGxIHEQlxfwCiVmlNyS2rpShliyH8eSURpSYS3vfUmb4lAwC4ZqVa60+lRTuv4SsAQ8iFnOBaPmxZUixBITcC0686wSnuosnxejdDaPwLfXR+j6C0zPtshqXglz+H9NBBCLlzT2ULJcbmPghnZM9PaB6TytGfKDvTfu5465XgkPfBFLzkVV3Yg3TiOxTB4L4XE2c7VrH+hXNHP4WVTrdRKGWKUgQpR1kQdWeFHyvkoovktCiN8JhN2wtipF798XfiOD-Jm49fmUMyLMm4OR-Q7VSu8NkhZvQDXtBKAmDzhVcsvpqEV3KQ68P-gOXK50QgjOKKbHaXUrbgvLO1DMfxQi5QesGTBvBkCEGRD81m0FLRunyO5UolqrL2TNlk9ePhTZ+gdPc7x2EvWCrebfE1iYSXmqinyTIBQkriT+FELKi88mWiKN4MINyMKjWTdIVNNFM2otrpeBu146KNsmLi8+n5B6Su4maghvzfBJWGmJb0GcHgOsQDHUCWQIIlvXKyz1E1MHNt5eRa+VFBWdrXYKntH9+1cWJUOmVrMsiXCyKuKIjKZmSUXncrJo4dqhF+Dcdeq7m7rsNbgs9MNWaLsFqKR4wy-Q7XGQgKclx-imxCIEMSHK9UNv3U239PDpUAaZElS4PhcrWv6rlBIxZoMxrg71ECSH621C7bnAJ61T7014BmntAbgKIStJzKs7UAQzqgyNBcXgRqSXXDcQMyGaOocmKU8pPkqm0AaU02grT2lvh6WY7Nw7AOHQXDKJ2RQxS5BlIvLwwoLhVntVyWI4mxp6IgHARwwZMMG1NH8PaY4iiQqnFQxA+RLhxDE6OW0UKHrLJ0qs5Yqg8DOYsWcT4u0q3ZSshKc40p4KhFw9zAzCNrT5Qky3FhPQYtkrNCEK0SVghiRXHFSDVXMZrgUQGZl4nqMFeUcxMmJFisDgKM5bMotuSi39MRl4TozLZhsqOXqu0IJFKlmFiq9mIDdcIU7NkToLTDQKOyfZYLChZI+ERpc2Sq1JvGj4nexSFsLT5St2GuUp6OWzBadyaUzoZEunkETt1RxzZztd8m314Bh3PYAy0TphPuXeMLelVto3-ClFWhdia-sk1Uzpea4xLAMDu6zAovhzQOQtA6fIXh7L7MFl4JHuVpSlFRweD28tonjCHrjpk2U9pJRS2+ohpyrbx38M9j4U3PRePO9hS7Us2EYJ7mffubPEBWV8L1LIbpNXnHSa6GFgt7QyUKLtXMjl6d7y6TLmi6yFevGghkF1IQ+TsnZFkKBothGlCdhbNKurWu+MKyo8qDANHPJeGPLDKQeoXSVfs3aWU6GpWKILGxWYNyeONwMTZQT84hK7pbt4WZBO7RVxEasw1E6ZOyXaD4oyAR5e95LnOMn2kVNVPJzpmeX458QpadcGFNVUuGpBiIXJMiHSGb1GUdYvfi5mOj5oEWLcg9DwgJVwo8nj7dI6LksOUgQMuYTm5fILRfrBBXTQmg4DA5Dy5sPgsRrHYpQ7R0KUSOmyjg8GUS5Vwtan7UEVJ+z+wAv3wVB1dGlF8H7wQQSkj011nFKFAlzEUjCHLxe05SD0t3eB5HhjyRjmSjt3glNgyB5FFjtF+BAzOzomNVvjQOlGaizHNETWGmL3gk-wultRp2glFGGhQMRQ3WiwXyv3JS8EwLEhCBwPZFVScjeESztUIKiC4JeUNTQMQmakVQh1yg1TBTXGdQwgtnxw9XyyFR4EwUtw8ArRXFFHiiVRKAR0XkkicgLBXDeBkiDTFz3W-TTQmEtwuSSl5wYQdFFBM3jydF6jCFFliHai-1cLBHXUPR4DQKcisSSmUgBBq2gKsMuACDFAgX6nXgiPvlowUL4NiwQEQgILniC3Xisj43chKA5kKHtzDWcSP0MMFXT0YzoEt2KEzAgmcQpSlE9DLQwh13wMLFuGSSaLwHXQb1mibzhHk0twNxHA+B1WoOENSOqxHACBQmRjkQqAqCAA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'availabilityRangeLoading',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => {
          return event.language;
        },
      }),
    },
  },
  states: {
    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id || eventDetails?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: eventDetails?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            selectedStartDateOfWeek: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: context.eventDetails?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              return new TZDate(startAt * 1000, context.timezone);
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },
    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            selectedStartDateOfWeek: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
        },
        'date.change': {
          actions: assign({
            selectedDate: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(addDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
        'week.prev': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(subDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                selectedStartDateOfWeek,
              },
            }) => {
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              try {
                if (
                  templateId &&
                  availabilityRange &&
                  selectedStartDateOfWeek
                ) {
                  const currentDate = selectedStartDateOfWeek;
                  const addedDate = addDays(currentDate, 7);
                  const startDate = formatISO(startOfDay(currentDate), {
                    representation: 'complete',
                  });
                  const endDate = formatISO(startOfDay(addedDate), {
                    representation: 'complete',
                  });

                  return {
                    templateId,
                    timezone,
                    eventId: eventDetails?.id,
                    sourceId: session?.source_id,
                    startDate,
                    endDate,
                  };
                }
                throw new Error('Template id or availabilityRange not found');
              } catch (error) {
                if (selectedStartDateOfWeek) {
                  captureSentryMessage(
                    'Error setting availability api params',
                    {
                      level: 'error',
                      tags: {product: 'scheduler'},
                      extra: {
                        error,
                        timezone,
                        eventDetails,
                        eventTemplate,
                        session,
                        availabilityRange,
                        selectedStartDateOfWeek,
                        weekStartDate: startOfDay(selectedStartDateOfWeek),
                        weekEndDate: startOfDay(
                          addDays(selectedStartDateOfWeek, 7),
                        ),
                      },
                    },
                  );
                }
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                timeSlots: ({context, event}) => ({
                  free_time_slots: {
                    ...context.timeSlots?.free_time_slots,
                    ...event.output.free_time_slots,
                  },
                }),
                selectedDate: ({context, event}) => {
                  if (!context.selectedStartDateOfWeek) {
                    throw new Error('Selected start date of week is not found');
                  }

                  return Object.keys(event.output?.free_time_slots || {})[0];
                },
                selectedTimeSlot: undefined,
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.submit': [
              {
                target: '#booking.bookingForm',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
                guard: {
                  type: 'isNewBooking',
                },
              },
              {
                target: 'timeslotConfirmation',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
              },
            ],
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.confirm': {
              target: 'rescheduleFormSubmitting',
            },
            'timeslot.submit': {
              target: 'timeslotConfirmation',
              actions: assign({
                selectedTimeSlot: ({event}) => event.timeSlot,
              }),
            },
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                timeSlots,
                attendee,
                selectedDate,
                selectedTimeSlot,
                timezone,
              },
            }) => {
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              if (eventDetails?.id && selectedDate) {
                return {
                  eventId: eventDetails?.id,
                  date: selectedDate,
                  startTime: selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                };
              }
              throw new Error('Event id not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'rescheduleFormError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        rescheduleFormError: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryReschedule',
              actions: assign({
                selectedTimeSlot: undefined,
              }),
              reenter: true,
            },
          ],
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
          },
        },

        retryReschedule: {
          on: {
            'timeslot.confirm': {
              target: 'rescheduleFormSubmitting',
              reenter: true,
            },
          },
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: 'availabilityLoading',
              reenter: true,
            },
          },
        },
      },
    },
    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },
    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },
    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },
    notify: {
      initial: 'form',
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {session, eventTemplate, eventDetails, timezone},
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },
    bookingForm: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                selectedDate,
                selectedTimeSlot,
                timeSlots,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;
              if (eventTemplate?.id && selectedDate && selectedTimeSlot) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date: selectedDate,
                  startTime: selectedTimeSlot.start_time,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                };
              }
              throw new Error('Template id, date or time not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState}}) => ({
    eventDetails,
    finalState,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
